/**
 * LUNA Coding Tutor - Monaco Fill-in-Blank Editor (Hybrid Approach)
 * 
 * Read-only Monaco Editor + React inline inputs
 * 마커를 placeholder로 교체하고, 값이 변경되면 실시간 반영
 */

import { useState, useEffect, useRef, forwardRef, useImperativeHandle, useMemo, useCallback } from 'react';
import Editor from '@monaco-editor/react';

// ============================================
// 유틸리티 함수
// ============================================

function formatCodeString(text) {
    if (!text) return '';
    return String(text).replace(/\\n/g, '\n').replace(/\\t/g, '\t');
}

function getLanguage(language) {
    const langMap = {
        'python': 'python',
        'javascript': 'javascript',
        'js': 'javascript',
        'html': 'html',
        'htmlmixed': 'html',
        'css': 'css',
    };
    return langMap[language?.toLowerCase()] || 'python';
}

function getFilename(language) {
    const filenameMap = {
        'python': 'main.py',
        'javascript': 'main.js',
        'html': 'index.html',
        'css': 'style.css',
    };
    return filenameMap[language] || 'main.py';
}

// 빈칸 마커 패턴
const BLANK_PATTERN = /__BLANK_(\d+)__/g;

// ============================================
// 메인 컴포넌트
// ============================================

const MonacoFillInBlankEditor = forwardRef(function MonacoFillInBlankEditor({
    template = '',
    language = 'python',
    onChange,
    onSubmit,
}, ref) {
    const [blankValues, setBlankValues] = useState({});
    const [blanks, setBlanks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const editorRef = useRef(null);
    const monacoRef = useRef(null);
    const blankValuesRef = useRef({});

    const monacoLanguage = useMemo(() => getLanguage(language), [language]);
    const filename = useMemo(() => getFilename(monacoLanguage), [monacoLanguage]);
    const formattedTemplate = useMemo(() => formatCodeString(template), [template]);

    // Keep ref in sync
    useEffect(() => {
        blankValuesRef.current = blankValues;
    }, [blankValues]);

    // 고유한 빈칸 추출 (중복 제거)
    useEffect(() => {
        const uniqueMarkers = new Map();
        const regex = new RegExp(BLANK_PATTERN.source, 'g');
        let match;

        while ((match = regex.exec(formattedTemplate)) !== null) {
            const marker = match[0];
            const id = match[1];
            if (!uniqueMarkers.has(marker)) {
                uniqueMarkers.set(marker, { id, marker });
            }
        }

        const extractedBlanks = Array.from(uniqueMarkers.values());
        const initialValues = {};
        extractedBlanks.forEach(b => {
            initialValues[b.marker] = '';
        });

        setBlanks(extractedBlanks);
        setBlankValues(initialValues);
    }, [formattedTemplate]);

    // 완성된 코드 생성
    const getCompletedCode = useCallback(() => {
        let result = formattedTemplate;
        Object.entries(blankValuesRef.current).forEach(([marker, value]) => {
            result = result.replace(new RegExp(marker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), value || '');
        });
        return result;
    }, [formattedTemplate]);

    // 표시용 코드 생성 (마커를 입력값 또는 placeholder로 교체)
    const displayCode = useMemo(() => {
        let result = formattedTemplate;
        Object.entries(blankValues).forEach(([marker, value]) => {
            const display = value || `【${marker.match(/__BLANK_(\d+)__/)?.[1] || '?'}】`;
            result = result.replace(new RegExp(marker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), display);
        });
        return result;
    }, [formattedTemplate, blankValues]);

    // 빈칸 값 변경 핸들러
    const handleBlankChange = useCallback((marker, value) => {
        setBlankValues(prev => {
            const newValues = { ...prev, [marker]: value };
            blankValuesRef.current = newValues;

            if (onChange) {
                let fullCode = formattedTemplate;
                Object.entries(newValues).forEach(([m, v]) => {
                    fullCode = fullCode.replace(new RegExp(m.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), v || '');
                });
                onChange(fullCode);
            }

            return newValues;
        });
    }, [onChange, formattedTemplate]);

    // Ref API 노출
    useImperativeHandle(ref, () => ({
        getCompletedCode,
        submit: () => getCompletedCode(),
        focus: () => {
            const firstInput = document.querySelector('.blank-input-field');
            if (firstInput) firstInput.focus();
        },
        setCode: () => { },
    }));

    // Monaco 마운트 핸들러
    const handleEditorDidMount = useCallback((editor, monaco) => {
        editorRef.current = editor;
        monacoRef.current = monaco;
        setIsLoading(false);

        // 커스텀 테마 등록
        monaco.editor.defineTheme('luna-blank', {
            base: 'vs-dark',
            inherit: true,
            rules: [],
            colors: {
                'editor.background': '#0f172a',
                'editor.foreground': '#e2e8f0',
                'editor.lineHighlightBackground': '#1e293b',
                'editorLineNumber.foreground': '#475569',
            }
        });
        monaco.editor.setTheme('luna-blank');
    }, []);

    return (
        <div className="monaco-fill-in-blank h-full flex flex-col">
            {/* IDE 스타일 헤더 */}
            <div className="ide-header flex items-center justify-between px-4 py-2 bg-slate-800/80 border-b border-slate-700/50 rounded-t-xl">
                <div className="flex items-center gap-3">
                    <div className="flex gap-1.5">
                        <span className="w-3 h-3 rounded-full bg-red-500" />
                        <span className="w-3 h-3 rounded-full bg-yellow-500" />
                        <span className="w-3 h-3 rounded-full bg-green-500" />
                    </div>
                    <span className="text-xs text-slate-400">{filename}</span>
                </div>
                <span className="text-xs text-cyan-400 font-medium">빈칸 채우기</span>
            </div>

            {/* 2열 레이아웃: 에디터 + 입력 패널 */}
            <div className="flex-1 flex overflow-hidden border border-cyan-500/30 rounded-b-xl">
                {/* 왼쪽: 코드 표시 (Monaco) */}
                <div className="flex-1 relative">
                    {isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-slate-900 text-slate-400 z-10">
                            <i className="fas fa-spinner fa-spin mr-2" />
                            에디터 로딩 중...
                        </div>
                    )}

                    <Editor
                        height="100%"
                        language={monacoLanguage}
                        value={displayCode}
                        onMount={handleEditorDidMount}
                        theme="vs-dark"
                        options={{
                            readOnly: true,
                            minimap: { enabled: false },
                            lineNumbers: 'on',
                            fontSize: 14,
                            fontFamily: "'Fira Code', 'Consolas', monospace",
                            scrollBeyondLastLine: false,
                            wordWrap: 'on',
                            automaticLayout: true,
                            padding: { top: 12, bottom: 12 },
                            renderLineHighlight: 'none',
                            cursorStyle: 'line',
                            domReadOnly: true,
                        }}
                    />
                </div>

                {/* 오른쪽: 빈칸 입력 패널 */}
                <div className="w-64 bg-slate-800/50 border-l border-slate-700 p-4 overflow-y-auto">
                    <div className="text-sm font-medium text-white mb-4 flex items-center gap-2">
                        <i className="fas fa-edit text-cyan-400" />
                        빈칸 입력
                    </div>

                    <div className="space-y-4">
                        {blanks.map((blank, index) => (
                            <div key={`blank-${blank.id}-${index}`} className="space-y-1.5">
                                <label className="text-xs text-slate-400 flex items-center gap-1">
                                    <span className="w-5 h-5 rounded bg-cyan-500/20 text-cyan-400 text-xs flex items-center justify-center font-bold">
                                        {blank.id}
                                    </span>
                                    <span>번 빈칸</span>
                                </label>
                                <input
                                    type="text"
                                    value={blankValues[blank.marker] || ''}
                                    onChange={(e) => handleBlankChange(blank.marker, e.target.value)}
                                    placeholder={`정답을 입력하세요`}
                                    className="blank-input-field w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/30 transition-colors"
                                />
                            </div>
                        ))}
                    </div>

                    {blanks.length === 0 && (
                        <div className="text-slate-500 text-sm text-center py-8">
                            빈칸이 없습니다
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});

export default MonacoFillInBlankEditor;
